import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { cn, formatCurrency, formatPercentage, getValueColor, getTrendDirection } from "@/lib/utils"
import { TrendingUp, TrendingDown, Minus } from "lucide-react"

interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  change?: number
  changeLabel?: string
  variant?: "default" | "profit" | "loss" | "neutral"
  format?: "currency" | "percentage" | "number"
  icon?: React.ReactNode
  className?: string
}

export function MetricCard({
  title,
  value,
  subtitle,
  change,
  changeLabel,
  variant = "default",
  format = "number",
  icon,
  className
}: MetricCardProps) {
  const formatValue = (val: string | number) => {
    if (typeof val === "string") return val
    
    switch (format) {
      case "currency":
        return formatCurrency(val)
      case "percentage":
        return formatPercentage(val)
      default:
        return val.toLocaleString()
    }
  }

  const getTrendIcon = (direction: "up" | "down" | "neutral") => {
    switch (direction) {
      case "up":
        return <TrendingUp className="h-4 w-4" />
      case "down":
        return <TrendingDown className="h-4 w-4" />
      default:
        return <Minus className="h-4 w-4" />
    }
  }

  const trendDirection = change !== undefined ? getTrendDirection(change) : "neutral"
  const changeColor = change !== undefined ? getValueColor(change) : "text-muted-foreground"

  return (
    <Card className={cn("relative overflow-hidden transition-all duration-200 hover:shadow-lg", className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {icon && (
          <div className="text-muted-foreground">
            {icon}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="text-2xl font-bold tracking-tight">
            {formatValue(value)}
          </div>
          
          {subtitle && (
            <p className="text-xs text-muted-foreground">
              {subtitle}
            </p>
          )}
          
          {change !== undefined && (
            <div className="flex items-center gap-2">
              <div className={cn("flex items-center gap-1 text-xs", changeColor)}>
                {getTrendIcon(trendDirection)}
                <span className="font-medium">
                  {formatPercentage(Math.abs(change))}
                </span>
              </div>
              {changeLabel && (
                <span className="text-xs text-muted-foreground">
                  {changeLabel}
                </span>
              )}
            </div>
          )}
        </div>
        
        {variant !== "default" && (
          <div className={cn(
            "absolute top-0 right-0 h-1 w-full",
            variant === "profit" && "bg-profit",
            variant === "loss" && "bg-loss", 
            variant === "neutral" && "bg-muted"
          )} />
        )}
      </CardContent>
    </Card>
  )
}